// Bodysocks: dynamic bottom fade on the basket drawer's scrolling product list.
// The scroll container is the inner .cart-form (see cart-drawer.liquid). We fade
// the bottom edge to hint there are more items below, and REMOVE the fade once
// the list is scrolled to the last item (or when everything already fits).
// Implemented as a custom element so it re-attaches automatically each time the
// cart drawer re-renders via the Section Rendering API.
class CartScrollFade extends HTMLElement {
  #raf = null;
  #scroller = null;
  #resizeObserver = null;

  connectedCallback() {
    this.#scroller = this.querySelector('.cart-form');
    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });

    // Re-evaluate when the list height changes (item added/removed) or the
    // drawer is resized — not just on scroll.
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.#scroller);
    const content = this.#scroller.firstElementChild;
    if (content) this.#resizeObserver.observe(content);

    this.#update();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    if (this.#raf !== null) cancelAnimationFrame(this.#raf);
  }

  #onScroll = () => {
    if (this.#raf !== null) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#update();
    });
  };

  #update = () => {
    const el = this.#scroller;
    if (!el) return;
    const scrollable = el.scrollHeight - el.clientHeight;
    // At the bottom (or nothing to scroll) → no fade. Otherwise fade the bottom.
    const atBottom = scrollable <= 1 || el.scrollTop >= scrollable - 1;
    const mask = atBottom
      ? ''
      : 'linear-gradient(to bottom, black calc(100% - 28px), transparent)';
    el.style.webkitMaskImage = mask;
    el.style.maskImage = mask;
  };
}

if (!customElements.get('cart-scroll-fade')) {
  customElements.define('cart-scroll-fade', CartScrollFade);
}
